import {
  Badge,
  Button,
  FileEditor,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Agent, CreateAgentDto, UpdateAgentDto } from './types';

// ─── Schema ───────────────────────────────────────────────────────────────────

const guardrailsSchema = z.object({
  approvalMode: z.enum(['NONE', 'REQUIRED', 'SUGGESTED']),
  maxToolCalls: z.coerce.number().int().positive('Must be greater than 0'),
  maxSteps: z.coerce.number().int().positive('Must be greater than 0'),
  allowedTools: z.array(z.string()).optional(),
});

const interfaceSchema = z.object({
  displayName: z.string().min(1, 'Display name is required'),
  shortDescription: z.string().min(1, 'Short description is required'),
});

const llmConfigSchema = z
  .object({
    temperature: z.coerce.number().min(0).max(2).optional().or(z.literal('')),
    maxTokens: z.coerce.number().int().positive().optional().or(z.literal('')),
    topP: z.coerce.number().min(0).max(1).optional().or(z.literal('')),
  })
  .optional();

const createSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only'),
  appSlug: z.string().min(1, 'App slug is required'),
  scope: z.enum(['PUBLIC', 'APPLICATION', 'TENANT']),
  type: z.enum(['TASK', 'CONVERSATIONAL']),
  status: z.enum(['ACTIVE', 'DRAFT', 'DEPRECATED']),
  modelAlias: z.string().min(1, 'Model alias is required'),
  systemPrompt: z.string().min(1, 'System prompt is required'),
  interface: interfaceSchema,
  guardrails: guardrailsSchema,
  llmConfig: llmConfigSchema,
  skillRefs: z.array(z.string()),
  workflowRefs: z.array(z.string()),
  toolRefs: z.array(z.string()),
  tags: z.array(z.string()),
  tenant: z.string().nullable().optional(),
});

const editSchema = createSchema
  .omit({ name: true, appSlug: true, scope: true, tenant: true })
  .partial();

type CreateFormValues = z.infer<typeof createSchema>;

// ─── Defaults ─────────────────────────────────────────────────────────────────

const CREATE_DEFAULTS: CreateFormValues = {
  appSlug: 'code-factory',
  name: '',
  scope: 'APPLICATION',
  type: 'TASK',
  status: 'ACTIVE',
  modelAlias: 'claude-sonnet-4-6',
  systemPrompt: '',
  interface: { displayName: '', shortDescription: '' },
  guardrails: { approvalMode: 'SUGGESTED', maxToolCalls: 20, maxSteps: 50, allowedTools: [] },
  llmConfig: {},
  skillRefs: [],
  workflowRefs: [],
  toolRefs: [],
  tags: [],
  tenant: null,
};

function agentToFormValues(agent: Agent): CreateFormValues {
  return {
    appSlug: agent.appSlug,
    name: agent.name,
    scope: agent.scope,
    type: agent.type,
    status: agent.status,
    modelAlias: agent.modelAlias,
    systemPrompt: agent.systemPrompt,
    interface: {
      displayName: agent.interface.displayName,
      shortDescription: agent.interface.shortDescription,
    },
    guardrails: {
      approvalMode: agent.guardrails.approvalMode,
      maxToolCalls: agent.guardrails.maxToolCalls,
      maxSteps: agent.guardrails.maxSteps,
      allowedTools: agent.guardrails.allowedTools ?? [],
    },
    llmConfig: {
      temperature: agent.llmConfig?.temperature,
      maxTokens: agent.llmConfig?.maxTokens,
      topP: agent.llmConfig?.topP,
    },
    skillRefs: [...agent.skillRefs],
    workflowRefs: [...agent.workflowRefs],
    toolRefs: [...agent.toolRefs],
    tags: [...agent.tags],
    tenant: agent.tenantSlug,
  };
}

// ─── Tag list input ───────────────────────────────────────────────────────────

function TagListInput({
  label,
  value,
  placeholder,
  onChange,
  disabled,
}: {
  label: string;
  value: string[];
  placeholder: string;
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');

  function addToken(raw: string) {
    if (disabled) return;
    const token = raw.trim();
    if (!token || value.includes(token)) {
      setDraft('');
      return;
    }
    onChange([...value, token]);
    setDraft('');
  }

  return (
    <FormItem>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <div className="space-y-2">
          {!disabled && (
            <div className="flex gap-2">
              <Input
                value={draft}
                placeholder={placeholder}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToken(draft);
                  }
                }}
              />
              <Button type="button" variant="secondary" onClick={() => addToken(draft)}>
                Add
              </Button>
            </div>
          )}
          {value.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {value.map((item) => (
                <Badge
                  key={item}
                  variant="secondary"
                  appearance="soft"
                  className={disabled ? 'gap-1' : 'gap-1 pr-1'}
                >
                  <span>{item}</span>
                  {!disabled && (
                    <button
                      type="button"
                      className="rounded-full p-0.5 hover:bg-muted"
                      aria-label={`Remove ${item}`}
                      onClick={() => onChange(value.filter((v) => v !== item))}
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </FormControl>
    </FormItem>
  );
}

// ─── AgentForm ────────────────────────────────────────────────────────────────

const MODEL_SUGGESTIONS = ['gpt-4o', 'claude-sonnet-4-6', 'claude-opus-4-7'];

interface AgentFormProps {
  mode: 'create' | 'edit' | 'view';
  initialValue?: Agent | null;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (payload: CreateAgentDto | UpdateAgentDto) => Promise<void> | void;
  /** When true, renders without Sheet-specific height/padding — for inline page use */
  inline?: boolean;
  /** External form id so a submit button outside the form can trigger submission */
  formId?: string;
}

export function AgentForm({
  mode,
  initialValue,
  submitting,
  onCancel,
  onSubmit,
  inline,
  formId,
}: AgentFormProps) {
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const ro = isView;
  const [showLlmConfig, setShowLlmConfig] = useState(false);

  const form = useForm<CreateFormValues>({
    resolver: isView
      ? undefined
      : zodResolver(isEdit ? (editSchema as unknown as typeof createSchema) : createSchema),
    defaultValues: initialValue ? agentToFormValues(initialValue) : CREATE_DEFAULTS,
  });

  const skillRefs = form.watch('skillRefs') ?? [];
  const workflowRefs = form.watch('workflowRefs') ?? [];
  const toolRefs = form.watch('toolRefs') ?? [];
  const tags = form.watch('tags') ?? [];
  const allowedTools = form.watch('guardrails.allowedTools') ?? [];

  async function onValid(data: CreateFormValues) {
    const llmConfig =
      data.llmConfig && Object.values(data.llmConfig).some((v) => v !== '' && v !== undefined)
        ? {
            temperature: data.llmConfig.temperature !== '' ? data.llmConfig.temperature : undefined,
            maxTokens: data.llmConfig.maxTokens !== '' ? data.llmConfig.maxTokens : undefined,
            topP: data.llmConfig.topP !== '' ? data.llmConfig.topP : undefined,
          }
        : undefined;

    const iface = {
      displayName: data.interface.displayName,
      shortDescription: data.interface.shortDescription,
    };

    const guardrails = {
      approvalMode: data.guardrails.approvalMode,
      maxToolCalls: data.guardrails.maxToolCalls,
      maxSteps: data.guardrails.maxSteps,
      ...(data.guardrails.allowedTools?.length
        ? { allowedTools: data.guardrails.allowedTools }
        : {}),
    };

    if (isEdit) {
      const payload: UpdateAgentDto = {
        type: data.type,
        status: data.status,
        modelAlias: data.modelAlias,
        systemPrompt: data.systemPrompt,
        interface: iface,
        guardrails,
        ...(llmConfig && { llmConfig }),
        skillRefs: data.skillRefs,
        workflowRefs: data.workflowRefs,
        toolRefs: data.toolRefs,
        tags: data.tags,
      };
      await onSubmit(payload);
    } else {
      const payload: CreateAgentDto = {
        appSlug: data.appSlug,
        name: data.name,
        scope: data.scope,
        type: data.type,
        status: data.status,
        modelAlias: data.modelAlias,
        systemPrompt: data.systemPrompt,
        interface: iface,
        guardrails,
        ...(llmConfig && { llmConfig }),
        skillRefs: data.skillRefs,
        workflowRefs: data.workflowRefs,
        toolRefs: data.toolRefs,
        tags: data.tags,
        tenant: data.tenant,
      };
      await onSubmit(payload);
    }
  }

  return (
    <Form {...form}>
      <form
        id={formId}
        className={inline ? 'w-full' : 'flex h-full flex-col'}
        onSubmit={form.handleSubmit(onValid)}
      >
        <div className={inline ? 'space-y-4 pb-2' : 'flex-1 overflow-y-auto px-6 pb-6 space-y-4'}>
          {/* ── Basic ──────────────────────────────────────────────────────── */}
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <h3 className="text-sm font-medium">Basic</h3>

            {!isEdit && (
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="agent-slug" {...field} disabled={ro} />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Lowercase letters, numbers, and hyphens only.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="interface.displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Cypher" {...field} disabled={ro} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="interface.shortDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description
                    <span className="ml-1 text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe what this agent does and its primary capabilities…"
                      rows={3}
                      className="resize-none"
                      {...field}
                      disabled={ro}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ── Deployment ─────────────────────────────────────────────────── */}
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <h3 className="text-sm font-medium">Deployment</h3>

            {!isEdit && (
              <FormField
                control={form.control}
                name="appSlug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>App Slug</FormLabel>
                    <FormControl>
                      <Input placeholder="code-factory" {...field} disabled={ro} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid gap-4 sm:grid-cols-3">
              {!isEdit && (
                <FormField
                  control={form.control}
                  name="scope"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Scope</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange} disabled={ro}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select scope" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="PUBLIC">Public</SelectItem>
                          <SelectItem value="APPLICATION">Application</SelectItem>
                          <SelectItem value="TENANT">Tenant</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="TASK">Task</SelectItem>
                        <SelectItem value="CONVERSATIONAL">Conversational</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="DRAFT">Draft</SelectItem>
                        <SelectItem value="DEPRECATED">Deprecated</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="modelAlias"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Model Alias</FormLabel>
                  <FormControl>
                    <Input
                      list="agent-model-aliases"
                      placeholder="claude-sonnet-4-6"
                      {...field}
                      disabled={ro}
                    />
                    <datalist id="agent-model-aliases">
                      {MODEL_SUGGESTIONS.map((m) => (
                        <option key={m} value={m} />
                      ))}
                    </datalist>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ── System Prompt ──────────────────────────────────────────────── */}
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <h3 className="text-sm font-medium">System Prompt</h3>
            <FormField
              control={form.control}
              name="systemPrompt"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="h-80">
                      <FileEditor
                        path="system-prompt.md"
                        content={field.value ?? ''}
                        onChange={field.onChange}
                        readOnly={ro}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ── Guardrails ─────────────────────────────────────────────────── */}
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <h3 className="text-sm font-medium">Guardrails</h3>

            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="guardrails.approvalMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Approval Mode</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="NONE">None</SelectItem>
                        <SelectItem value="REQUIRED">Required</SelectItem>
                        <SelectItem value="SUGGESTED">Suggested</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="guardrails.maxToolCalls"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Tool Calls</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} {...field} disabled={ro} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="guardrails.maxSteps"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Steps</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} {...field} disabled={ro} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <TagListInput
              label="Allowed Tools"
              value={allowedTools}
              placeholder="terminal.exec"
              onChange={(next) => form.setValue('guardrails.allowedTools', next)}
              disabled={ro}
            />
          </div>

          {/* ── Refs & Tags ────────────────────────────────────────────────── */}
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <h3 className="text-sm font-medium">Refs & Tags</h3>
            <TagListInput
              label="Skill Refs"
              value={skillRefs}
              placeholder="skills/w50_delivery"
              onChange={(next) => form.setValue('skillRefs', next)}
              disabled={ro}
            />
            <TagListInput
              label="Workflow Refs"
              value={workflowRefs}
              placeholder="delivery-code"
              onChange={(next) => form.setValue('workflowRefs', next)}
              disabled={ro}
            />
            <TagListInput
              label="Tool Refs"
              value={toolRefs}
              placeholder="ado-client"
              onChange={(next) => form.setValue('toolRefs', next)}
              disabled={ro}
            />
            <TagListInput
              label="Tags"
              value={tags}
              placeholder="code-review"
              onChange={(next) => form.setValue('tags', next)}
              disabled={ro}
            />
          </div>

          {/* ── LLM Config (optional) ──────────────────────────────────────── */}
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <button
              type="button"
              className="flex w-full items-center justify-between"
              onClick={() => setShowLlmConfig((v) => !v)}
            >
              <h3 className="text-sm font-medium">LLM Config</h3>
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${showLlmConfig ? 'rotate-180' : ''}`}
              />
            </button>

            {showLlmConfig && (
              <div className="grid gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="llmConfig.temperature"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Temperature</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          min={0}
                          max={2}
                          placeholder="0.7"
                          {...field}
                          value={field.value ?? ''}
                          disabled={ro}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="llmConfig.maxTokens"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Tokens</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          placeholder="4096"
                          {...field}
                          value={field.value ?? ''}
                          disabled={ro}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="llmConfig.topP"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Top P</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.05"
                          min={0}
                          max={1}
                          placeholder="0.9"
                          {...field}
                          value={field.value ?? ''}
                          disabled={ro}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        </div>

        {!inline && (
          <div className="border-t px-6 py-4 flex items-center justify-end gap-2">
            {isView ? (
              <Button type="button" variant="secondary" onClick={onCancel}>
                Close
              </Button>
            ) : (
              <>
                <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Agent'}
                </Button>
              </>
            )}
          </div>
        )}
      </form>
    </Form>
  );
}
